/**
 * Canonical Page Document Model
 *
 * Hierarchy: Page -> Section -> Container -> Widget
 *
 * Every node has:
 * - id: Stable unique identifier (e.g. sec_xyz, con_xyz, wgt_xyz)
 * - type: 'section' | 'container' | widget_type (e.g. 'heading', 'text', 'card', 'product_rail')
 * - name: Human-readable label for the layers navigator and outline badge
 * - content: Widget/node-specific text, images, links, queries
 * - style: Typography, colors, alignment, spacing, borders, radius, etc.
 * - responsive: Viewport-specific overrides ({ tablet: {...}, mobile: {...} })
 * - children: Array of child nodes (sections hold containers; containers hold widgets)
 * - visibility: { desktop: boolean, tablet: boolean, mobile: boolean }
 * - collectionRef: Optional reference to an existing CMS collection/record
 */

export function generateId(prefix = 'node') {
  const rand = Math.random().toString(36).substring(2, 9);
  const time = Date.now().toString(36).slice(-4);
  return `${prefix}_${rand}${time}`;
}

export function createDefaultSection(name = 'Section') {
  return {
    id: generateId('sec'),
    type: 'section',
    name,
    content: {},
    style: {
      backgroundColor: '#ffffff',
      paddingTop: '40px',
      paddingBottom: '40px',
      paddingLeft: '24px',
      paddingRight: '24px',
      borderWidth: '0px',
      borderColor: '#e2e8f0',
      borderStyle: 'solid',
    },
    responsive: {
      tablet: { paddingTop: '32px', paddingBottom: '32px' },
      mobile: { paddingTop: '24px', paddingBottom: '24px' },
    },
    visibility: { desktop: true, tablet: true, mobile: true },
    children: [createDefaultContainer('1-column')],
  };
}

export function createDefaultContainer(layout = '1-column', name = 'Container') {
  return {
    id: generateId('con'),
    type: 'container',
    name,
    layout, // '1-column', '2-column', '3-column', '4-column', 'sidebar-left', 'sidebar-right'
    content: {},
    style: {
      maxWidth: '1200px',
      margin: '0 auto',
      gap: '24px',
      padding: '0px',
    },
    responsive: {
      tablet: { gap: '16px' },
      mobile: { gap: '16px' },
    },
    visibility: { desktop: true, tablet: true, mobile: true },
    children: [],
  };
}

export function createDefaultWidget(widgetType, customProps = {}) {
  const baseName = widgetType.charAt(0).toUpperCase() + widgetType.slice(1).replace(/_/g, ' ');
  return {
    id: generateId('wgt'),
    type: widgetType,
    name: customProps.name || baseName,
    content: customProps.content || {},
    style: customProps.style || {},
    responsive: customProps.responsive || {
      tablet: {},
      mobile: {},
    },
    visibility: customProps.visibility || { desktop: true, tablet: true, mobile: true },
    collectionRef: customProps.collectionRef || null,
  };
}

export const DEFAULT_HEADER_CONFIG = {
  website_name: 'ZOBOX',
  logo_url: 'https://zobox-website-staging-6cgwjq3laq-as.a.run.app/brand/zobox-header-logo.gif',
  logo_alt: 'ZOBOX',
  logo_href: '/',
  support_label: 'Support',
  support_url: '/support',
  search_placeholder: 'Search devices...',
  background_color: '#2B3380',
  text_color: '#FFFFFF',
  padding_y: '14px',
  padding_x: '24px',
  show_search: true,
  show_actions: true,
  show_support: true,
  nav_items: [
    { id: 'buy', title: 'Buy', url: '/buy', enabled: true },
    { id: 'sell', title: 'Sell', url: '/sell', enabled: true },
    { id: 'zobiz', title: 'ZOBIZ', url: '/partner', enabled: true },
    { id: 'stores', title: 'Stores', url: '/stores', enabled: true },
    { id: 'about', title: 'About Us', url: '/about', enabled: true },
    { id: 'contact', title: 'Contact', url: '/contact', enabled: true },
    { id: 'warranty', title: 'Warranty', url: '/warranty', enabled: true },
  ],
};

export const DEFAULT_FOOTER_CONFIG = {
  prompt: 'Got questions? Just ask.',
  brand_logo: 'https://zobox-website-staging-6cgwjq3laq-as.a.run.app/brand/zobox-header-logo.gif',
  background_color: '#FFFFFF',
  text_color: '#334155',
  padding_top: '48px',
  padding_bottom: '32px',
  show_social: true,
  show_actions: true,
  show_offices: true,
  show_disclaimer: true,
  actions: [
    { id: 'act_1', label: 'Open Support', href: '/support', variant: 'primary' },
    { id: 'act_2', label: 'Contact us', href: '/contact', variant: 'secondary' },
  ],
  social_label: 'Follow us on',
  social_links: [
    { id: 'soc_fb', platform: 'facebook', label: 'Facebook', url: 'https://www.facebook.com/my.zobox' },
    { id: 'soc_ig', platform: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/my.zobox' },
    { id: 'soc_li', platform: 'linkedin', label: 'LinkedIn', url: 'https://www.linkedin.com/company/yourzobox/' },
    { id: 'soc_yt', platform: 'youtube', label: 'YouTube', url: 'https://youtube.com/@zoboxsales' },
  ],
  columns: [
    {
      id: 'col_ecosystem',
      title: 'Ecosystem',
      links: [
        { id: 'l1', label: 'Buy', href: '/buy' },
        { id: 'l2', label: 'Sell', href: '/sell' },
        { id: 'l3', label: 'ZOBIZ', href: '/partner' },
        { id: 'l4', label: 'Corporate', href: '/corporate' },
        { id: 'l5', label: 'Franchise', href: '/franchise' },
      ],
    },
    {
      id: 'col_company',
      title: 'Company',
      links: [
        { id: 'l6', label: 'About', href: '/about' },
        { id: 'l7', label: 'ZOBOX Minutes', href: '/minutes' },
        { id: 'l8', label: 'Stores', href: '/stores' },
        { id: 'l9', label: 'Contact', href: '/contact' },
      ],
    },
    {
      id: 'col_help',
      title: 'Help & Support',
      links: [
        { id: 'l10', label: 'Support', href: '/support' },
        { id: 'l11', label: 'Warranty', href: '/warranty' },
        { id: 'l12', label: 'FAQ', href: '/faq' },
        { id: 'l13', label: 'Returns & refunds', href: '/returnRefundPolicy' },
        { id: 'l14', label: 'Privacy', href: '/privacyPolicy' },
      ],
    },
  ],
  office_locations: [
    { id: 'off_1', label: 'Registered office', address: '4th Floor, 6/77, WEA, Karol Bagh, New Delhi – 110005' },
    { id: 'off_2', label: 'Corporate office', address: 'E218C, Sector 63, Noida, Uttar Pradesh – 201301' },
  ],
  disclaimer: 'ZOBOX employees will never ask for your OTP over phone or email. Please do not share your OTP with anyone.',
  copyright: '© 2025 Zobox Protech Limited. All rights reserved.',
};

export function createBlankPageDocument(titleOrOptions = 'Untitled Page', slug = '', route = '') {
  let title = 'Untitled Page';
  let s = slug;
  let r = route;
  let status = 'draft';

  if (typeof titleOrOptions === 'object' && titleOrOptions !== null) {
    title = titleOrOptions.title || 'Untitled Page';
    s = titleOrOptions.slug || '';
    r = titleOrOptions.route || (s ? `/${s}` : '');
    status = titleOrOptions.status || 'draft';
  } else {
    title = titleOrOptions || 'Untitled Page';
    r = route || (s ? `/${s}` : '');
  }

  return {
    version: 1,
    id: generateId('page'),
    title,
    slug: s,
    route: r,
    layout: 'custom',
    status,
    navbar_label: '',
    is_active: true,
    display_order: 0,
    header: JSON.parse(JSON.stringify(DEFAULT_HEADER_CONFIG)),
    footer: JSON.parse(JSON.stringify(DEFAULT_FOOTER_CONFIG)),
    seo: {
      title: '',
      description: '',
      keywords: '',
      og_image: '',
      canonical_url: '',
    },
    sections: [createDefaultSection('Main Section')],
  };
}

/**
 * Deep clones an element and regenerates unique IDs for all child nodes.
 */
export function duplicateNode(node) {
  if (!node) return null;
  const clone = JSON.parse(JSON.stringify(node));
  const prefix = clone.type === 'section' ? 'sec' : clone.type === 'container' ? 'con' : 'wgt';
  clone.id = generateId(prefix);
  clone.name = `${clone.name || clone.type} (Copy)`;

  if (Array.isArray(clone.children)) {
    clone.children = clone.children.map(duplicateNode);
  }
  return clone;
}

/**
 * Validates canonical document hierarchy and constraints.
 */
export function validateDocument(doc) {
  const errors = [];
  if (!doc) {
    errors.push('Document is missing');
    return { isValid: false, errors };
  }
  if (!doc.title || !String(doc.title).trim()) {
    errors.push('Page title is required');
  }
  if (!doc.slug || !String(doc.slug).trim()) {
    errors.push('Page slug is required and must be entered manually');
  } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(doc.slug))) {
    errors.push('Slug can use lowercase letters, numbers and single hyphens only');
  }
  if (doc.route && !String(doc.route).startsWith('/')) {
    errors.push('Route must start with a slash (e.g. /about)');
  }
  if (!Array.isArray(doc.sections) || doc.sections.length === 0) {
    errors.push('Page must have at least one section');
  } else {
    doc.sections.forEach((sec, sIdx) => {
      if (!sec.id) errors.push(`Section #${sIdx + 1} is missing an ID`);
      if (sec.type !== 'section') errors.push(`Node #${sIdx + 1} is not a valid section`);
      if (Array.isArray(sec.children)) {
        sec.children.forEach((con, cIdx) => {
          if (con.type !== 'container') {
            errors.push(`Child of Section #${sIdx + 1} at position ${cIdx + 1} must be a container`);
          }
        });
      }
    });
  }
  return { isValid: errors.length === 0, errors };
}

/**
 * Find element and its parent chain in document by ID.
 */
export function findNodeById(sections, id) {
  if (!sections || !id) return null;
  for (const sec of sections) {
    if (sec.id === id) return { node: sec, parent: null, parentArray: sections, index: sections.indexOf(sec) };
    if (Array.isArray(sec.children)) {
      for (const con of sec.children) {
        if (con.id === id) return { node: con, parent: sec, parentArray: sec.children, index: sec.children.indexOf(con) };
        if (Array.isArray(con.children)) {
          for (const wgt of con.children) {
            if (wgt.id === id) return { node: wgt, parent: con, parentArray: con.children, index: con.children.indexOf(wgt) };
          }
        }
      }
    }
  }
  return null;
}
